import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    HorizontalFlex,
    ButtonSecondary,
    Button,
} from 'smartsuite-ui';
import TimeAndPrecisionSelector from './TimeAndPrecisionSelector';
import Filters from './Filters';
import MeasureFilters from './MeasureFilters';
import { useLabelContext } from '../hooks/labelContext';
import {
    getIsFixedRangeInOrder,
    getIsFixedRangeValid,
    getIsLastNextRangeValid,
    requiredFiltersHaveValues,
    areWhereClausesValid,
    isRecordLimitValid,
    getIsPrecisionValid,
} from './filtersModalUtils';
import RecordLimit from './RecordLimit';
import DashboardContext from '../hooks/dashboardContext';
import { validateTimeAndPrecision } from './timeAndPrecisionValidation';

export default function WidgetFiltersModal(props) {
    const { translationLabels, dashboardLabels, interpolateString } =
        useLabelContext();
    const { selectOptions } = useContext(DashboardContext);

    const [updatedTimeAndPrecision, setUpdatedTimeAndPrecision] = useState(
        props.timeAndPrecision
    );
    const [updatedFilterValues, setUpdatedFilterValues] = useState(
        props.filterValues
    );
    const [updatedWhereClauses, setUpdatedWhereClauses] = useState(
        props.whereClauses
    );
    const [updatedRecordLimit, setUpdatedRecordLimit] = useState({
        recordLimit: props.recordLimit,
        isDescending: !!props.isDescending,
    });

    function handleSave() {
        props.handleSave(
            updatedTimeAndPrecision,
            updatedFilterValues,
            updatedWhereClauses,
            updatedRecordLimit
        );
        props.closeModal();
    }

    function handleReset() {
        props.handleReset();
        props.closeModal();
    }

    /* eslint-disable complexity */
    function isSaveDisabled() {
        const isFixedRangeInOrder = getIsFixedRangeInOrder(
            updatedTimeAndPrecision
        );
        const isFixedRangeValid = getIsFixedRangeValid(
            updatedTimeAndPrecision,
            props.timeAndPrecisionRangeDomains
        );
        const isLastNextRangeValid = getIsLastNextRangeValid(
            updatedTimeAndPrecision,
            props.timeAndPrecisionRangeDomains
        );
        const isPrecisionValid = getIsPrecisionValid(
            updatedTimeAndPrecision,
            props.isGauge
        );
        const { isTimeAndPrecisionValid } = validateTimeAndPrecision(
            updatedTimeAndPrecision,
            interpolateString,
            dashboardLabels,
            props.isGauge
        );

        return (
            !isFixedRangeInOrder ||
            !isFixedRangeValid ||
            !isLastNextRangeValid ||
            !isPrecisionValid ||
            !isTimeAndPrecisionValid ||
            !requiredFiltersHaveValues(
                props.requiredFilterKeys,
                updatedFilterValues,
                props.availableFilters,
                selectOptions
            ) ||
            !areWhereClausesValid(updatedWhereClauses) ||
            !isRecordLimitValid(updatedRecordLimit)
        );
    }
    /* eslint-enable complexity */

    return (
        <Modal onOverlayClick={() => {}} size="large">
            <ModalHeader onDismiss={props.closeModal}>
                {props.title}
            </ModalHeader>
            <ModalBody>
                <div className="widget-filters">
                    <TimeAndPrecisionSelector
                        isGauge={props.isGauge}
                        precisionTypeOptions={props.precisionTypeOptions}
                        predefinedDateRanges={props.predefinedDateRanges}
                        saveTimeAndPrecision={setUpdatedTimeAndPrecision}
                        showPrecisionConfig={props.showPrecisionConfig}
                        showTimeAndPrecision={props.showTimeAndPrecision}
                        timeAndPrecision={updatedTimeAndPrecision}
                        timeAndPrecisionTypeOptions={
                            props.timeAndPrecisionTypeOptions
                        }
                    />
                    <Filters
                        additionalFilterKeys={props.additionalFilterKeys}
                        availableFilters={props.availableFilters}
                        filterValues={updatedFilterValues}
                        requiredFilterKeys={props.requiredFilterKeys}
                        saveFilters={setUpdatedFilterValues}
                    />
                    <MeasureFilters
                        legendFormatter={props.legendFormatter}
                        measures={props.whereClauseOptions}
                        saveWhereClauses={setUpdatedWhereClauses}
                        showMeasureFilters={props.showMeasureFilters}
                        whereClauses={[
                            ...updatedWhereClauses,
                            {
                                values: [''],
                            },
                        ]}
                    />
                    <RecordLimit
                        showRecordLimit={props.showRecordLimit}
                        recordLimitConfig={updatedRecordLimit}
                        saveRecordLimit={setUpdatedRecordLimit}
                    />
                </div>
            </ModalBody>
            <ModalFooter>
                <HorizontalFlex cssClassModifiers={['space-between']}>
                    <ButtonSecondary onClick={handleReset}>
                        {dashboardLabels.restoreDefault}
                    </ButtonSecondary>
                    <HorizontalFlex>
                        <Button
                            onClick={handleSave}
                            disabled={isSaveDisabled()}
                        >
                            {translationLabels.apply}
                        </Button>
                        <ButtonSecondary onClick={props.closeModal}>
                            {translationLabels.cancel}
                        </ButtonSecondary>
                    </HorizontalFlex>
                </HorizontalFlex>
            </ModalFooter>
        </Modal>
    );
}

WidgetFiltersModal.propTypes = {
    additionalFilterKeys: PropTypes.instanceOf(Object).isRequired,
    availableFilters: PropTypes.instanceOf(Object),
    closeModal: PropTypes.func.isRequired,
    filterValues: PropTypes.instanceOf(Object).isRequired,
    handleReset: PropTypes.func.isRequired,
    handleSave: PropTypes.func.isRequired,
    isDescending: PropTypes.bool.isRequired,
    isGauge: PropTypes.bool.isRequired,
    legendFormatter: PropTypes.func.isRequired,
    precisionTypeOptions: PropTypes.instanceOf(Object),
    predefinedDateRanges: PropTypes.instanceOf(Array),
    recordLimit: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
    requiredFilterKeys: PropTypes.instanceOf(Array),
    showMeasureFilters: PropTypes.bool.isRequired,
    showPrecisionConfig: PropTypes.bool.isRequired,
    showRecordLimit: PropTypes.bool.isRequired,
    showTimeAndPrecision: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    timeAndPrecision: PropTypes.instanceOf(Object).isRequired,
    timeAndPrecisionRangeDomains: PropTypes.instanceOf(Object),
    timeAndPrecisionTypeOptions: PropTypes.instanceOf(Array),
    whereClauses: PropTypes.instanceOf(Array).isRequired,
    whereClauseOptions: PropTypes.instanceOf(Array).isRequired,
};

WidgetFiltersModal.defaultProps = {
    availableFilters: {},
    predefinedDateRanges: [],
    requiredFilterKeys: [],
    timeAndPrecisionTypeOptions: undefined,
};
